import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Optional inline label rendered after the box. Omit to render a bare box. */
  label?: ReactNode;
  invalid?: boolean;
}

const boxClasses =
  'h-4 w-4 shrink-0 rounded border-[var(--border-strong)] ' +
  'accent-[var(--button-primary-bg)] ' +
  'transition-colors duration-fast ease-standard ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--field-focus-ring)] ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

/**
 * Checkbox field primitive. Shares the field focus-ring + accent tokens so it
 * matches Input/Select. Pass `label` for the common inline-label layout, or
 * omit it to render just the box (e.g. inside a custom row).
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { className, label, invalid = false, disabled, ...rest },
  ref,
) {
  const box = (
    <input
      ref={ref}
      type="checkbox"
      aria-invalid={invalid || undefined}
      disabled={disabled}
      className={cn(boxClasses, className)}
      {...rest}
    />
  );
  if (label === undefined) return box;
  return (
    <label
      className={cn(
        'inline-flex items-center gap-2 text-sm text-[var(--text-primary)]',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      {box}
      <span>{label}</span>
    </label>
  );
});
